import React, { useState, useEffect } from 'react';
import { Rss, Hash, Loader, Copy, Check, Info, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/axios';

const SlackIntegrationSettings = () => {
    const [feedUrl, setFeedUrl] = useState('');
    const [hasToken, setHasToken] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    const [loading, setLoading] = useState(true);
    const [savingWebhook, setSavingWebhook] = useState(false);
    const [generatingFeed, setGeneratingFeed] = useState(false);
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [copiedFeed, setCopiedFeed] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            // Fetch Webhook config
            const slackRes = await api.get('/api/integrations/slack/config');
            setWebhookUrl(slackRes.data.slackWebhookUrl || '');
            setNotificationsEnabled(slackRes.data.slackNotificationsEnabled || false);

            // Fetch RSS feed config
            const feedRes = await api.get('/api/integrations/slack/rss/url');
            setFeedUrl(feedRes.data.feedUrl || '');
            setHasToken(feedRes.data.hasToken || false);

        } catch (error) {
            toast.error('Failed to load integration settings');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateFeed = async () => {
        try {
            setGeneratingFeed(true);
            const res = await api.post('/api/integrations/slack/rss/token');
            setFeedUrl(res.data.feedUrl);
            setHasToken(true);
            toast.success('New RSS feed URL generated');
        } catch (error) {
            toast.error('Failed to generate feed URL');
        } finally {
            setGeneratingFeed(false);
        }
    };

    const handleCopyFeed = () => {
        if (!feedUrl) return;
        navigator.clipboard.writeText(feedUrl);
        setCopiedFeed(true);
        toast.success('Feed URL copied to clipboard');
        setTimeout(() => setCopiedFeed(false), 2000);
    };

    const handleSaveWebhook = async () => {
        try {
            setSavingWebhook(true);
            const res = await api.put('/api/integrations/slack/webhook', {
                webhookUrl,
                enabled: notificationsEnabled
            });
            setWebhookUrl(res.data.slackWebhookUrl);
            setNotificationsEnabled(res.data.slackNotificationsEnabled);
            toast.success('Slack webhook settings saved');
        } catch (error) {
            toast.error('Failed to save webhook settings');
        } finally {
            setSavingWebhook(false);
        }
    };

    const handleRemoveWebhook = async () => {
        try {
            setSavingWebhook(true);
            await api.put('/api/integrations/slack/webhook', {
                webhookUrl: '',
                enabled: false
            });
            setWebhookUrl('');
            setNotificationsEnabled(false);
            toast.success('Slack webhook removed');
        } catch (error) {
            toast.error('Failed to remove webhook settings');
        } finally {
            setSavingWebhook(false);
        }
    };

    const handleTestWebhook = async () => {
        if (!webhookUrl) return;
        try {
            setTestingWebhook(true);
            await api.post('/api/integrations/slack/test', { webhookUrl });
            toast.success('Test notification sent successfully');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to send test notification');
        } finally {
            setTestingWebhook(false);
        }
    };

    if (loading) {
        return (
            <div className="card-premium p-6 mt-8 flex justify-center items-center h-48">
                <Loader className="w-8 h-8 animate-spin text-brand-600" />
            </div>
        );
    }

    return (
        <div className="card-premium p-6 mt-8">
            <div className="flex items-center space-x-3 mb-6 border-b border-surface-200 dark:border-surface-800 pb-4">
                <div className="p-2.5 bg-[#E01E5A]/10 rounded-xl">
                    <Hash className="h-6 w-6 text-[#E01E5A]" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-surface-900 dark:text-white">Slack Integrations</h2>
                    <p className="text-sm text-surface-500 font-medium">Connect Uptime Monitor to your Slack workspace for incident alerts</p>
                </div>
            </div>

            <div className="space-y-8">

                {/* Method 1: RSS Feed via /feed */}
                <div className="bg-surface-50 dark:bg-surface-900/50 rounded-xl p-5 border border-surface-200 dark:border-surface-800">
                    <div className="flex items-center space-x-3 mb-4">
                        <Rss className="h-5 w-5 text-orange-500" />
                        <h3 className="text-lg font-semibold text-surface-900 dark:text-white">Method 1: Slack RSS Feed App</h3>
                    </div>

                    <div className="space-y-4">
                        <p className="text-sm text-surface-600 dark:text-surface-400">
                            Use the official Slack RSS App to subscribe to a private feed of your incidents. In any Slack channel, type: <code className="bg-surface-200 dark:bg-surface-800 px-1.5 py-0.5 rounded text-[#E01E5A] text-xs">/feed subscribe [YOUR_FEED_URL]</code>
                        </p>

                        <div className="flex items-center space-x-2">
                            <input
                                type="text"
                                readOnly
                                value={feedUrl || 'Generate a feed URL to get started'}
                                className="input-premium flex-1 font-mono text-sm"
                            />
                            <button
                                onClick={handleCopyFeed}
                                disabled={!hasToken}
                                className="btn-secondary !p-3 disabled:opacity-50"
                                title="Copy URL"
                            >
                                {copiedFeed ? <Check className="w-5 h-5 text-success" /> : <Copy className="w-5 h-5" />}
                            </button>
                        </div>

                        <div className="flex justify-between items-center">
                            <button
                                onClick={handleGenerateFeed}
                                disabled={generatingFeed}
                                className="btn-primary !py-2 !px-4 text-sm"
                            >
                                {generatingFeed ? (
                                    <><Loader className="w-4 h-4 mr-2 animate-spin inline-block" /> Generating...</>
                                ) : (
                                    hasToken ? 'Regenerate Feed URL' : 'Generate Feed URL'
                                )}
                            </button>

                            {hasToken && (
                                <p className="text-xs text-surface-400 font-medium flex items-center">
                                    <Info className="w-3 h-3 mr-1" />
                                    Regenerating will invalidate your old URL.
                                </p>
                            )}
                        </div>

                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-lg flex items-start space-x-3">
                            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                                <strong>Note:</strong> You must install the <a href="https://slack.com/apps/A0F81R7U7-rss" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800 dark:hover:text-blue-300">RSS App from the Slack Directory</a> into your workspace before using the <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">/feed</code> command.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Method 2: Incoming Webhooks */}
                <div className="bg-surface-50 dark:bg-surface-900/50 rounded-xl p-5 border border-surface-200 dark:border-surface-800">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 rounded-full bg-[#E01E5A] shadow-[0_0_8px_rgba(224,30,90,0.6)] animate-pulse" />
                            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">Method 2: Incoming Webhook (Recommended)</h3>
                        </div>

                        <label className="flex items-center cursor-pointer">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={notificationsEnabled}
                                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                                />
                                <div className={`block w-10 h-6 rounded-full transition-colors ${notificationsEnabled ? 'bg-brand-500' : 'bg-surface-300 dark:bg-surface-700'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${notificationsEnabled ? 'transform translate-x-4' : ''}`}></div>
                            </div>
                        </label>
                    </div>

                    <div className="space-y-4">
                        <p className="text-sm text-surface-600 dark:text-surface-400">
                            For instant, richly-formatted alerts with downtime durations and error messages, provide a Slack Incoming Webhook URL.
                        </p>

                        <div>
                            <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                                Webhook URL
                            </label>
                            <input
                                type="text"
                                value={webhookUrl}
                                onChange={(e) => setWebhookUrl(e.target.value)}
                                placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX"
                                className="input-premium w-full"
                            />
                        </div>

                        <div className="flex items-center space-x-3 pt-2">
                            <button
                                onClick={handleSaveWebhook}
                                disabled={savingWebhook}
                                className="btn-primary"
                            >
                                {savingWebhook ? (
                                    <><Loader className="w-4 h-4 mr-2 animate-spin inline-block" /> Saving...</>
                                ) : (
                                    'Save Webhook'
                                )}
                            </button>

                            <button
                                onClick={handleTestWebhook}
                                disabled={testingWebhook || !webhookUrl}
                                className="btn-secondary"
                            >
                                {testingWebhook ? (
                                    <><Loader className="w-4 h-4 mr-2 animate-spin inline-block" /> Testing...</>
                                ) : (
                                    'Send Test Notification'
                                )}
                            </button>

                            {webhookUrl && (
                                <button
                                    onClick={handleRemoveWebhook}
                                    className="p-2 ml-auto text-danger hover:bg-danger/10 rounded-lg transition-colors"
                                    title="Remove webhook"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SlackIntegrationSettings;
